
import React, { useState } from 'react';
import './WhoisLookup.css';

const WhoisLookup = () => {
  const [query, setQuery] = useState('8.8.8.8');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleLookup = async () => {
    setLoading(true);
    try {
      const res = await fetch('/diagnostic-tools/whois-lookup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query })
      });
      const data = await res.json();
      if (data.status === 'success') setResult(data.data);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  const getCountry = (code) => {
    const countries = {
      BD: 'Bangladesh',
      US: 'United States',
      IN: 'India',
      SG: 'Singapore',
      GB: 'United Kingdom'
    };
    return countries[code] || code || 'N/A';
  };

  const InfoItem = ({ label, value }) => (
    <div className="whois-info-item">
      <span>{label}</span>
      <strong>{value || 'N/A'}</strong>
    </div>
  );

  return (
    <div className="tool-container p-3 whois-lookup">

      <h4>🔍 WHOIS & ASN Lookup</h4>

      <div className="input-group mb-3">
        <input
          type="text"
          className="form-control"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Enter IP or Domain (e.g. 8.8.8.8)"
        />

        <button
          className="btn btn-success"
          onClick={handleLookup}
          disabled={loading}
        >
          {loading ? 'Searching...' : 'Lookup'}
        </button>
      </div>

      {result && (
        <div className="whois-results">

          {/* ASN OVERVIEW */}
          <div className="whois-asn-card">
            <div className="whois-asn-top">
              <span className="whois-asn-label">AUTONOMOUS SYSTEM NUMBER</span>
              <span className="whois-live-badge">● LOOKUP SUCCESS</span>
            </div>

            <h2>AS{result.asn || 'N/A'}</h2>

            <h5>{result.asn_description || 'Unknown Organization'}</h5>

            <div className="whois-asn-meta">
              <span>🌐 {result.asn_registry?.toUpperCase() || 'N/A'}</span>
              <span>🌍 {getCountry(result.asn_country_code)}</span>
              <span>📅 {result.asn_date || 'N/A'}</span>
            </div>
          </div>

          {/* QUICK INFORMATION */}
          <div className="whois-section">
            <div className="whois-section-title">
              <span>🌐</span>
              <h5>IP Address Information</h5>
            </div>

            <div className="whois-info-grid">
              <InfoItem label="QUERY ADDRESS" value={result.query} />
              <InfoItem label="ASN" value={`AS${result.asn || 'N/A'}`} />
              <InfoItem label="ASN CIDR" value={result.asn_cidr} />
              <InfoItem label="COUNTRY" value={getCountry(result.asn_country_code)} />
            </div>
          </div>

          {/* NETWORK DETAILS */}
          {result.network && (
            <div className="whois-section">
              <div className="whois-section-title">
                <span>📡</span>
                <h5>Network Information</h5>
              </div>

              <div className="whois-info-grid">
                <InfoItem label="NETWORK NAME" value={result.network.name} />
                <InfoItem label="NETWORK CIDR" value={result.network.cidr} />
                <InfoItem label="START ADDRESS" value={result.network.start_address} />
                <InfoItem label="END ADDRESS" value={result.network.end_address} />
                <InfoItem label="IP VERSION" value={result.network.ip_version?.toUpperCase()} />
                <InfoItem label="NETWORK TYPE" value={result.network.type} />
                <InfoItem label="STATUS" value={result.network.status?.join(', ')} />
                <InfoItem label="COUNTRY" value={getCountry(result.network.country)} />
                <InfoItem label="HANDLE" value={result.network.handle} />
                <InfoItem label="PARENT HANDLE" value={result.network.parent_handle} />
              </div>

              {result.network.links?.length > 0 && (
                <div className="whois-links">
                  <span>RDAP NETWORK RECORD</span>
                  {result.network.links.map((link, index) => (
                    <a key={index} href={link} target="_blank" rel="noreferrer">
                      <span>{link}</span> ↗
                    </a>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ORGANIZATION / CONTACT DETAILS */}
          {result.objects && Object.keys(result.objects).length > 0 && (
            <div className="whois-section">
              <div className="whois-section-title">
                <span>🏢</span>
                <h5>Organization & Contact Details</h5>
              </div>

              <div className="whois-contact-grid">
                {Object.entries(result.objects).map(([key, obj]) => (
                  <div className="whois-contact-card" key={key}>

                    <div className="whois-contact-header">
                      <div>
                        <h6>{obj.contact?.name || key}</h6>
                        <span>{obj.handle || key}</span>
                      </div>

                      <span className="whois-contact-icon">🏢</span>
                    </div>

                    <div className="whois-contact-body">

                      {obj.roles?.length > 0 && (
                        <InfoItem
                          label="CONTACT ROLE"
                          value={obj.roles.join(' / ')}
                        />
                      )}

                      {obj.contact?.kind && (
                        <InfoItem label="CONTACT TYPE" value={obj.contact.kind} />
                      )}

                      {obj.contact?.address?.map((address, i) => (
                        <InfoItem
                          key={i}
                          label="ADDRESS"
                          value={address.value}
                        />
                      ))}

                      {obj.contact?.email?.map((email, i) => (
                        <InfoItem
                          key={i}
                          label="EMAIL"
                          value={email.value}
                        />
                      ))}

                      {obj.contact?.phone?.map((phone, i) => (
                        <InfoItem
                          key={i}
                          label="PHONE"
                          value={phone.value}
                        />
                      ))}

                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* REGISTRATION EVENTS */}
          {result.network?.events?.length > 0 && (
            <div className="whois-section">
              <div className="whois-section-title">
                <span>📅</span>
                <h5>Network Registration History</h5>
              </div>

              <div className="whois-events">
                {result.network.events.map((event, index) => (
                  <div className="whois-event" key={index}>
                    <div className="whois-event-dot"></div>
                    <div>
                      <strong>{event.action}</strong>
                      <span>
                        {event.timestamp
                          ? new Date(event.timestamp).toLocaleString()
                          : 'N/A'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* NETWORK ENTITIES */}
          {result.entities?.length > 0 && (
            <div className="whois-section">
              <div className="whois-section-title">
                <span>🔗</span>
                <h5>Registered Entities</h5>
              </div>

              <div className="whois-entities">
                {result.entities.map((entity, index) => (
                  <span key={index}>{entity}</span>
                ))}
              </div>
            </div>
          )}

          {/* FOOTER */}
          <div className="whois-result-footer">
            <span>WHOIS / RDAP INTELLIGENCE</span>
            <span>DATA SOURCE: {result.asn_registry?.toUpperCase() || 'RDAP'}</span>
          </div>

        </div>
      )}

    </div>
  );
};

export default WhoisLookup;
